import api from "./api";

// Club tier capabilities, mirrors backend lib/clubTier.ts. `limits` use null
// for "unlimited"; `flags` are on/off feature gates.
export type ClubCapabilities = {
  limits: { clubs: number | null; coaches: number | null; athletes: number | null };
  flags: {
    metrics: boolean;
    advancedReports: boolean;
    unlimitedProducts: boolean;
    trainingNotifications: boolean;
    paymentNotifications: boolean;
    customBranding: boolean;
    walletPremiumTools: boolean;
  };
  support: "basic" | "priority";
  trialDays: number | null;
};

export type ClubFeatureFlag = keyof ClubCapabilities["flags"];

/**
 * Where a club stands with its trial, as the server computed it.
 *
 * "locked" never means anything was removed: the club keeps every screen and
 * every record, and only writing stops until it moves to a paid plan.
 */
export type ClubAccessState = "paid" | "trial" | "trial_ending" | "locked";

export type ClubAccess = {
  clubId: string;
  clubName: string;
  planCode: string;
  state: ClubAccessState;
  trialEndsAt: string | null;
  daysLeft: number | null;
  canWrite: boolean;
};

// Club type definition with all extended fields (updated)
export type Club = {
  id: string;
  name: string;

  // Business Information
  legal_name?: string | null;
  cui?: string | null;
  cis_code?: string | null;
  registration_number?: string | null;
  business_address?: string | null;
  bank_account?: string | null;
  bank_name?: string | null;

  // Public Information
  public_name?: string | null;
  physical_address?: string | null;
  city?: string | null;
  county?: string | null;
  country?: string | null;
  website?: string | null;
  description?: string | null;
  logo_url?: string | null;
  brand_color?: string | null; // Premium: primary brand accent (hex)
  brand_color_secondary?: string | null; // Premium: secondary brand color (hex)
  social_media?: Record<string, string> | null;
  sports?: string[] | null;
  gallery?: string[] | null;

  // System
  status?: string;
  plan_code: string;
  // Opt-in: only clubs large enough to need a per-sport coordinator turn this
  // on, and until they do the role does not exist for them anywhere in the UI.
  coordinators_enabled?: boolean;
  // End of the free trial; null on a paid plan. See `access` for what it means.
  trial_ends_at?: string | null;
  access?: ClubAccess;
  capabilities?: ClubCapabilities; // tier capabilities (from /auth/me)
  cost_per_athlete_cents?: number; // Cost per athlete per month in cents (default 100 = 1 EUR)
  created_at: string;
  updated_at?: string;

  // Computed fields
  member_count?: number;
  athlete_count?: number;
  team_count?: number;
  /// Coaches with a profile at this club.
  coach_count?: number;
  /// How many sports the club has turned on for itself.
  sports_count?: number;
  /// The club's administrator, when it has one. Null is itself the signal:
  /// a club nobody administers cannot do anything.
  club_admin?: {
    id: string;
    email: string;
    full_name: string | null;
    status?: string | null;
  } | null;
};

export type ClubMember = {
  user: {
    id: string;
    email: string;
    full_name: string | null;
    created_at: string;
  };
  role: {
    id: string;
    code: string;
    label: string;
  };
  joined_at: string;
};

/**
 * Get club details by ID
 */
export async function getClubById(clubId: string) {
  const { data } = await api.get<Club>(`/clubs/${clubId}`);
  return data;
}

/**
 * Create new club (platform admin only)
 */
export async function createClub(payload: {
  name: string;
  logo_url?: string;
  description?: string;
  address?: string;
  plan_code?: string;
}) {
  const { data } = await api.post<Club>("/clubs", payload);
  return data;
}

/**
 * A Premium club_admin creates an ADDITIONAL club they own (self-serve).
 * Requires the caller to already administer at least one Premium club.
 */
export async function createOwnClub(payload: {
  name: string;
  legal_name?: string;
  city?: string;
  county?: string;
  sports?: string[];
}) {
  const { data } = await api.post<Club>("/clubs/self", payload);
  return data;
}

/**
 * Update club details
 */
export async function updateClub(
  clubId: string,
  payload: {
    // Basic
    name?: string;
    plan_code?: string;
    status?: string;
    // Billing (platform admin only)
    cost_per_athlete_cents?: number;
    // Business
    legal_name?: string;
    cui?: string;
    cis_code?: string;
    registration_number?: string;
    business_address?: string;
    bank_account?: string;
    bank_name?: string;
    // Public
    public_name?: string;
    physical_address?: string;
    city?: string;
    county?: string;
    country?: string;
    website?: string;
    description?: string;
    logo_url?: string;
    social_media?: Record<string, string>;
    sports?: string[];
  }
) {
  const { data } = await api.patch<Club>(`/clubs/${clubId}`, payload);
  return data;
}

/**
 * Delete club (soft delete, platform admin only)
 */
export async function deleteClub(clubId: string) {
  const { data } = await api.delete(`/clubs/${clubId}`);
  return data;
}

/**
 * Restart the 14-day demo trial for every club admin of this club.
 * Platform admin only.
 */
export async function restartClubDemo(clubId: string) {
  const { data } = await api.post<{ ok: boolean; affected_admins: number; demo_expires_at: string }>(
    `/clubs/${clubId}/restart-demo`,
  );
  return data;
}

/**
 * Put the club on a paid plan: the club's plan, price and trial, plus its
 * admins' accounts. Platform admin only.
 *
 * The plan is a required argument. "Activate" used to mean only "mark the
 * admins active", which changed nothing a club could feel — the trial gate and
 * the billing both read the club's plan_code.
 */
export async function activateClub(clubId: string, planCode: "basic" | "premium") {
  const { data } = await api.post<{
    ok: boolean;
    affected_admins: number;
    plan_code: string;
    plan_name: string;
    cost_per_athlete_cents: number;
  }>(`/clubs/${clubId}/activate`, { plan_code: planCode });
  return data;
}

/**
 * The opposite: off the paid plan and suspended. Nothing is deleted — the club
 * keeps its data and its people keep their accounts; its administrators cannot
 * sign in until it is activated or put back on demo.
 */
export async function deactivateClub(clubId: string) {
  const { data } = await api.post<{ ok: boolean; affected_admins: number; status: string }>(
    `/clubs/${clubId}/deactivate`,
  );
  return data;
}

/**
 * Get club members
 */
export async function getClubMembers(clubId: string, role?: string) {
  const qs = role ? `?role=${role}` : "";
  const { data } = await api.get<ClubMember[]>(`/clubs/${clubId}/members${qs}`);
  return data;
}

/**
 * Add member to club
 */
export async function addClubMember(
  clubId: string,
  payload: {
    user_id: string;
    role_code: string;
  }
) {
  const { data } = await api.post(`/clubs/${clubId}/members`, payload);
  return data;
}

/**
 * Remove member from club
 */
export async function removeClubMember(clubId: string, userId: string) {
  const { data } = await api.delete(`/clubs/${clubId}/members/${userId}`);
  return data;
}

/**
 * Update team
 */
export async function updateTeam(
  teamId: string,
  payload: {
    name?: string;
    age_group?: string;
    sport_type?: string;
    coach_id?: string;
  }
) {
  const { data } = await api.patch(`/teams/${teamId}`, payload);
  return data;
}

/**
 * Delete team (soft delete)
 */
export async function deleteTeam(teamId: string) {
  const { data } = await api.delete(`/teams/${teamId}`);
  return data;
}

/**
 * Ask a human to switch the club to the Basic plan.
 *
 * There is no self-serve club checkout yet, so the button has to reach the
 * people who can do it: this opens a high-priority support ticket, and calling
 * it twice does not open two.
 */
export async function requestClubActivation(
  clubId: string
): Promise<{ ok: boolean; already_requested?: boolean; ticket_id: string }> {
  const { data } = await api.post(`/clubs/${clubId}/request-activation`, {});
  return data;
}
